import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import { Button } from "@/components/ui/button";
import { PermissionButton } from "@/components/ui/permission-button";
import { CreateTaskDialog } from "@/components/CreateTaskDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Ticket,
  Filter,
  Clock,
  CalendarDays,
  User,
  MessageSquare,
  AlertTriangle,
  CheckCircle,
  Plus,
  Search,
  Eye,
  FileText,
  Tag,
  Calendar,
  Building2,
  UserCircle,
  Sparkles,
  UserPlus,
  ListChecks,
  ChevronDown,
  ChevronRight,
  GitMerge,
  Link,
  X,
  Check,
  ChevronsUpDown
} from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { useAuth } from "@/hooks/useAuth";
import { format } from "date-fns";
import { cn, parseDateLocal } from "@/lib/utils";
import { isEnrollmentRequestTask } from "@/lib/enrollmentTasks";
import { getWaiveReasonLabel } from "@shared/utils/waiveReasons";
import { isTaskOverdue } from "@shared/lib/taskStatus";
import { priorityColors, statusColors } from "@/lib/taskColors";
import { TaskHeaderPills, TaskChipRow } from "@/components/tasks/TaskControls";

// Legacy placeholder email for auto-generated (system) tasks — never show it.
const FAKE_SYSTEM_EMAIL = "system@administr.com";
// What to show under the requester name: the real email, or "Auto-created" for
// system-generated tasks (which have no real requester), or nothing.
const requesterSubtitle = (t: { requesterEmail?: string | null; requesterType?: string }) => {
  if (t.requesterEmail && t.requesterEmail !== FAKE_SYSTEM_EMAIL) return t.requesterEmail;
  if (t.requesterType === "system") return "Auto-created";
  return null;
};

// Local types for API responses
type TicketsListResponse = {
  tasks: any[];
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalTasks: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
};

type TicketStats = {
  total: number;
  open: number;
  inProgress: number;
  pendingInfo: number;
  resolved: number;
  closed: number;
  byPriority: Record<string, number>;
  averageResolutionTime: string;
};

type TicketDetail = {
  id: number;
  ticketNumber?: string;
  taskNumber?: string;
  title: string;
  description: string;
  priority: string;
  status: string;
  createdAt: string | Date;
  dueDate?: string | Date | null;
  comments?: Array<{ id: number; content: string; authorName: string; createdAt: string | Date; isInternal?: boolean }>;
  history?: Array<{ id: number; changeType: string; fieldName?: string | null; oldValue?: string | null; newValue?: string | null; description: string; createdAt: string | Date }>;
  requesterName: string;
  requesterEmail?: string;
  requesterType?: string;
  clientId?: number | null;
  clientName?: string | null;
  tags?: string[];
  checklistItems?: Array<{
    id: number;
    taskId: number;
    label: string;
    isCompleted: boolean;
    completedAt?: string | null;
    completedById?: string | null;
    sortOrder: number;
  }>;
  parentTaskId?: number | null;
  source?: string | null;
  subtasks?: Array<{ id: number; taskNumber: string; title: string; status: string; priority: string }>;
  parentTask?: { id: number; taskNumber: string; title: string; status: string } | null;
};

export default function Tickets() {
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [selectedPriority, setSelectedPriority] = useState<string>("all");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedAssignment, setSelectedAssignment] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(20);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [clientPopoverOpen, setClientPopoverOpen] = useState(false);
  const [expandedParents, setExpandedParents] = useState<Set<number>>(new Set());
  const [pendingCloseWarning, setPendingCloseWarning] = useState<{ status: string; openCount: number } | null>(null);
  const [createSubtaskParent, setCreateSubtaskParent] = useState<{ id: number; clientId?: number | null; taskNumber: string } | null>(null);
  const [linkTaskNumber, setLinkTaskNumber] = useState("");
  const [isLinking, setIsLinking] = useState(false);
  const [enrollDenyDialogOpen, setEnrollDenyDialogOpen] = useState(false);
  const [enrollDenyReason, setEnrollDenyReason] = useState("");
  const [denyTarget, setDenyTarget] = useState<"enrollment" | "qle">("enrollment");
  const [subtasksOpen, setSubtasksOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const { toast } = useToast();
  const { user } = useAuth();
  const currentUserId = user?.id;

  const getScopeBadge = (ticket: any) => {
    const scope = ticket.taskScope || (ticket.clientId ? 'team' : 'personal');
    if (scope === 'team') {
      return (
        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800">
          <Building2 className="h-3 w-3 mr-1" />
          Team
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800">
        <UserCircle className="h-3 w-3 mr-1" />
        Personal
      </Badge>
    );
  };

  const getSourceBadge = (ticket: any) => {
    const source = ticket.source || 'manual';
    if (source === 'ai_suggested' || source === 'ai_auto') {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800">
                  <Sparkles className="h-3 w-3 mr-1" />
                  AI
                </Badge>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>This task was {source === 'ai_auto' ? 'automatically created' : 'suggested'} by AI</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    if (source === 'system') {
      return (
        <Badge variant="outline" className="text-xs bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-900/20 dark:text-gray-300 dark:border-gray-800">
          System
        </Badge>
      );
    }
    return null;
  };

  // Check if ticket is enrollment-related (by category or title)
  const isEnrollmentRelatedTicket = (ticket: any): boolean => {
    const category = ticket.category || '';
    const title = (ticket.title || '').toLowerCase();
    return category === 'benefits_enrollment' || 
           title.includes('enrollment') || 
           title.includes('enroll');
  };

  // Get enrollment progress badge styling
  const getEnrollmentProgressBadge = (ticket: any) => {
    const progress = ticket.enrollmentProgress;
    if (!progress || !isEnrollmentRelatedTicket(ticket)) return null;

    const progressConfig: Record<string, { bg: string; text: string; label: string }> = {
      not_started: { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-700 dark:text-gray-300', label: 'Not Started' },
      viewed: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', label: 'Viewed' },
      in_progress: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-300', label: 'In Progress' },
      submitted: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300', label: 'Submitted' },
      under_review: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', label: 'Under Review' },
      confirmed: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', label: 'Confirmed' },
      declined: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', label: 'Declined' },
    };

    const config = progressConfig[progress];
    if (!config) return null;

    return (
      <Badge className={`${config.bg} ${config.text} text-xs font-medium`}>
        {config.label}
      </Badge>
    );
  };

  // Enrollment-request task that's been approved but is still open while the broker
  // works the carrier checklist. Make that state obvious in the list + detail.
  const isEnrollmentApproved = (ticket: any) =>
    isEnrollmentRequestTask(ticket) &&
    (ticket?.customFields as any)?.enrollmentApproved === true;

  const getEnrollmentApprovedBadge = (ticket: any) =>
    isEnrollmentApproved(ticket) ? (
      <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 text-xs font-medium">
        <CheckCircle className="h-3 w-3 mr-1" />
        Approved · finish checklist
      </Badge>
    ) : null;

  // Handle URL query params for opening create dialog or task detail
  useEffect(() => {
    const params = new URLSearchParams(searchString);
    const createParam = params.get("create");
    const openParam = params.get("open");

    if (createParam === "true") {
      setIsCreateDialogOpen(true);
      // Clear the query param
      setLocation("/tasks", { replace: true });
    }

    if (openParam) {
      const ticketId = parseInt(openParam, 10);
      if (!isNaN(ticketId)) {
        setSelectedTicketId(ticketId);
        setIsDetailDialogOpen(true);
        // Clear the query param
        setLocation("/tasks", { replace: true });
      }
    }
  }, [searchString, setLocation]);
  const queryClient = useQueryClient();
  // This page's list is cached under "tasks"; other pages (e.g. Reports.tsx) read the
  // same data under "/api/tasks" — invalidate both, or those pages never refresh.
  const invalidateTaskQueries = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["tasks"], exact: false }),
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"], exact: false }),
    ]);

  // Clients list for task detail editing
  const { data: clients = [] } = useQuery<any[]>({ queryKey: ["/api/clients"] });

  // Fetch team members for assignment dropdown (filtered by team if client selected)
  const { data: teamMembersData } = useQuery({
    queryKey: ["tasks", "team-members"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/tasks/team-members");
      return res.json();
    },
  });

  const teamMembers = teamMembersData?.teamMembers || [];

  const { data: ticketsData, isLoading } = useQuery<TicketsListResponse>({
    queryKey: [
      "tasks",
      {
        status: selectedStatus !== "all" ? selectedStatus : undefined,
        priority: selectedPriority !== "all" ? selectedPriority : undefined,
        category: selectedCategory !== "all" ? selectedCategory : undefined,
        assignmentStatus: selectedAssignment !== "all" ? selectedAssignment : undefined,
        q: searchTerm || undefined,
        page,
        limit,
      },
    ],
    queryFn: async ({ queryKey }) => {
      const [_key, params] = queryKey as [string, Record<string, any>];
      const url = new URL("/api/tasks", window.location.origin);
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, String(v));
      });
      const res = await fetch(url.toString(), { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
    placeholderData: keepPreviousData,
  });

  // Fetch ticket statistics
  const { data: stats } = useQuery<TicketStats>({ queryKey: ["/api/tasks/stats"] });

  // Details query (fetch when dialog open)
  const { data: ticketDetail, refetch: refetchTicketDetail, isFetching: isFetchingDetail } = useQuery<TicketDetail>({
    queryKey: selectedTicketId ? [`/api/tasks/${selectedTicketId}`] : ["/api/tasks/idle"],
    enabled: !!selectedTicketId && isDetailDialogOpen,
  });

  // Update ticket mutation
  const updateTicketMutation = useMutation({
    mutationFn: async (updates: Partial<{ status: string; priority: string; assignedTo: string | null; parentTaskId: number | null; forceClose?: boolean; clientId: number | null; taskScope: string; dueDate: string | null }>) => {
      if (!selectedTicketId) return;
      const res = await apiRequest("PATCH", `/api/tasks/${selectedTicketId}`, updates);
      return res.json();
    },
    onSuccess: async () => {
      await Promise.all([
        invalidateTaskQueries(),
        refetchTicketDetail(),
      ]);
      toast({ title: "Updated", description: "Task updated" , variant: "success"});
    },
    onError: (error: any) => {
      // Handle open subtasks warning — show confirmation dialog instead of toast
      if (error.statusCode === 409 && error.details?.code === 'OPEN_SUBTASKS') {
        setPendingCloseWarning({ status: error.details.requestedStatus || 'closed', openCount: error.details.openSubtaskCount });
        return;
      }

      const message = error.message || "Failed to update task";
      const isPermissionError = error.message?.includes("permission") || error.message?.includes("agency owner");

      toast({
        title: isPermissionError ? "Permission Denied" : "Error",
        description: message,
        variant: "destructive"
      });
    },
  });

  // Assign to me mutation (for quick assignment from list)
  const assignToMeMutation = useMutation({
    mutationFn: async (ticketId: number) => {
      const res = await apiRequest("PATCH", `/api/tasks/${ticketId}`, {
        assignedTo: currentUserId
      });
      return res.json();
    },
    onSuccess: async (_data, ticketId) => {
      await invalidateTaskQueries();
      await queryClient.invalidateQueries({ queryKey: ["/api/tasks/stats"] });
      // Also refresh the open detail panel, else it still shows "unassigned".
      await queryClient.invalidateQueries({ queryKey: [`/api/tasks/${ticketId}`] });
      toast({ title: "Assigned", description: "Task assigned to you", variant: "success" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to assign task", variant: "destructive" });
    },
  });

  const handleAssignToMe = (ticketId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    assignToMeMutation.mutate(ticketId);
  };

  // Add comment mutation
  const addCommentMutation = useMutation({
    mutationFn: async (payload: { content: string }) => {
      if (!selectedTicketId) return;
      const authorName = user?.firstName && user?.lastName
        ? `${user.firstName} ${user.lastName}`
        : user?.email || "Broker";
      const authorEmail = user?.email || "";
      const res = await apiRequest('POST', `/api/tasks/${selectedTicketId}/comments`, {
        content: payload.content,
        authorName,
        authorEmail,
        authorType: 'broker'
      });
      return res.json();
    },
    onSuccess: async () => {
      await refetchTicketDetail();
      toast({ title: 'Comment added' , variant: "success"});
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to add comment', variant: 'destructive' });
    }
  });

  // Toggle checklist item mutation
  const toggleChecklistMutation = useMutation({
    mutationFn: async ({ taskId, itemId, isCompleted }: { taskId: number; itemId: number; isCompleted: boolean }) => {
      const res = await apiRequest('PATCH', `/api/tasks/${taskId}/checklist/${itemId}`, { isCompleted });
      return res.json();
    },
    onSuccess: async () => {
      await refetchTicketDetail();
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to update checklist', variant: 'destructive' });
    }
  });

  const tickets = ticketsData?.tasks || [];
  const pagination = ticketsData?.pagination;

  // Group tasks: hide children under their parent when parent is on the same page
  const parentIdsOnPage = new Set(tickets.filter((t: any) => !t.parentTaskId).map((t: any) => t.id));
  const childTasksByParent: Record<number, any[]> = {};
  const topLevelTickets: any[] = [];
  for (const ticket of tickets) {
    if (ticket.parentTaskId && parentIdsOnPage.has(ticket.parentTaskId)) {
      (childTasksByParent[ticket.parentTaskId] ||= []).push(ticket);
    } else {
      topLevelTickets.push(ticket);
    }
  }

  const toggleExpandParent = (parentId: number) => {
    setExpandedParents(prev => {
      const next = new Set(prev);
      if (next.has(parentId)) next.delete(parentId);
      else next.add(parentId);
      return next;
    });
  };

  const openTicket = (ticketId: number, edit = false) => {
    setSelectedTicketId(ticketId);
    setIsEditMode(edit);
    setIsDetailDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Tasks</h1>
          <p className="text-muted-foreground mt-1">
            Manage client and employee support requests
          </p>
        </div>
        <PermissionButton
          permission="tasks:create"
          whenDenied="hide"
          onClick={() => setIsCreateDialogOpen(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Task
        </PermissionButton>
        <CreateTaskDialog
          open={isCreateDialogOpen}
          onOpenChange={(open) => {
            setIsCreateDialogOpen(open);
            if (!open) setCreateSubtaskParent(null);
          }}
          parentTask={createSubtaskParent}
          onCreated={() => {
            if (createSubtaskParent) {
              refetchTicketDetail();
              setCreateSubtaskParent(null);
            }
          }}
        />
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Tasks</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Ticket className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Open</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {stats.open}
                  </p>
                </div>
                <AlertTriangle className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">In Progress</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {stats.inProgress}
                  </p>
                </div>
                <Clock className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Resolved</p>
                  <p className="text-2xl font-bold text-green-600">
                    {stats.resolved}
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Avg Resolution
                  </p>
                  <p className="text-2xl font-bold">
                    {stats.averageResolutionTime}
                  </p>
                </div>
                <Calendar className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => {
                  setPage(1);
                  setSearchTerm(e.target.value);
                }}
                className="w-64"
              />
            </div>
            <Select value={selectedStatus} onValueChange={(v) => { setPage(1); setSelectedStatus(v); }}>
              <SelectTrigger className="w-40"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="pending_info">Pending Info</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedPriority} onValueChange={(v) => { setPage(1); setSelectedPriority(v); }}>
              <SelectTrigger className="w-40"><SelectValue placeholder="Priority" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedCategory} onValueChange={(v) => { setPage(1); setSelectedCategory(v); }}>
              <SelectTrigger className="w-48"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="benefits_enrollment">
                  Benefits Enrollment
                </SelectItem>
                <SelectItem value="plan_changes">Plan Changes</SelectItem>
                <SelectItem value="claims">Claims</SelectItem>
                <SelectItem value="billing">Billing</SelectItem>
                <SelectItem value="general">General</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedAssignment} onValueChange={(v) => { setPage(1); setSelectedAssignment(v); }}>
              <SelectTrigger className="w-48"><SelectValue placeholder="Assignment" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tasks</SelectItem>
                <SelectItem value="assigned_to_me">Assigned to Me</SelectItem>
                <SelectItem value="assigned_to_others">Assigned to Others</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="border-t pt-4">
            {isLoading ? (
              <div className="text-sm text-muted-foreground">Loading tasks...</div>
            ) : topLevelTickets.length === 0 ? (
              <div className="text-sm text-muted-foreground">No tasks found.</div>
            ) : (
              <div className="space-y-4">
                {topLevelTickets.map((ticket: any) => {
                  const isExpanded = expandedParents.has(ticket.id);
                  const children = childTasksByParent[ticket.id] || [];
                  const hasSubtasks = ticket.subtaskCount > 0 || children.length > 0;
                  return (
                    <div key={ticket.id}>
                      <div className="p-4 border rounded-lg hover:bg-muted/50 transition cursor-pointer" onClick={() => openTicket(ticket.id)}>
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                          <div className="space-y-2 flex-1">
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className="font-mono text-sm text-muted-foreground">{ticket.taskNumber || ticket.ticketNumber}</span>
                              <Badge className={`text-xs ${priorityColors[ticket.priority as keyof typeof priorityColors]}`}>{String(ticket.priority).toUpperCase()}</Badge>
                              <Badge className={`text-xs ${statusColors[ticket.status as keyof typeof statusColors]}`}>{String(ticket.status).replace("_", " ").toUpperCase()}</Badge>
                              {getScopeBadge(ticket)}
                              {getSourceBadge(ticket)}
                              {getEnrollmentProgressBadge(ticket)}
                              {getEnrollmentApprovedBadge(ticket)}
                              {ticket.assignedTo && (() => {
                                const assignee = teamMembers.find((m: any) => m.id === ticket.assignedTo);
                                return assignee ? (
                                  <Badge variant="outline" className="text-xs">
                                    <User className="h-3 w-3 mr-1" />
                                    {assignee.firstName} {assignee.lastName}
                                  </Badge>
                                ) : null;
                              })()}
                            </div>
                            <h3 className="font-medium">{ticket.title}</h3>
                            {(ticket as any).resolution ? (
                              <div className="flex items-center gap-1.5 text-sm text-green-700 dark:text-green-400">
                                <CheckCircle className="h-3.5 w-3.5" />
                                <span>{(ticket as any).resolution}</span>
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground line-clamp-2">{ticket.description}</p>
                            )}
                            <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                              {ticket.clientName && (
                                <div className="flex items-center gap-1"><FileText className="h-3 w-3" />{ticket.clientName}</div>
                              )}
                              <div className="flex items-center gap-1"><User className="h-3 w-3" />{ticket.requesterName}</div>
                              {ticket.dueDate ? (
                                (() => {
                                  // parseDateLocal: dueDate is date-only; naive
                                  // new Date() shifts it a day in negative-UTC zones
                                  const due = parseDateLocal(ticket.dueDate as any);
                                  const overdue = isTaskOverdue(ticket);
                                  return (
                                    <div
                                      className={`flex items-center gap-1 ${overdue ? "text-red-600 dark:text-red-400 font-medium" : ""}`}
                                      title={`Created ${format(new Date(ticket.createdAt), "MMM d, yyyy")}`}
                                    >
                                      <CalendarDays className="h-3 w-3" />
                                      Due {due ? format(due, "MMM d, yyyy") : ""}
                                      {overdue && " · Overdue"}
                                    </div>
                                  );
                                })()
                              ) : (
                                <div
                                  className="flex items-center gap-1"
                                  title={`Created ${format(new Date(ticket.createdAt), "MMM d, yyyy")}`}
                                >
                                  <Clock className="h-3 w-3" />
                                  Created {format(new Date(ticket.createdAt), "MMM d, yyyy")}
                                </div>
                              )}
                              {ticket.tags && ticket.tags.length > 0 && (
                                <div className="flex items-center gap-1"><Tag className="h-3 w-3" />{ticket.tags.slice(0,2).join(", ")}{ticket.tags.length>2 && '...'} </div>
                              )}
                              {hasSubtasks && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); toggleExpandParent(ticket.id); }}
                                  className="flex items-center gap-1 text-blue-600 hover:underline dark:text-blue-400"
                                >
                                  {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                  <GitMerge className="h-3 w-3" />
                                  {ticket.subtaskCount} subtask{ticket.subtaskCount !== 1 ? 's' : ''} ({ticket.subtaskCompletedCount} done)
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 md:ml-4">
                            {!ticket.assignedTo && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => handleAssignToMe(ticket.id, e)}
                                      disabled={assignToMeMutation.isPending}
                                      className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                    >
                                      <UserPlus className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Assign to me</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openTicket(ticket.id, false); }}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                      {/* Expanded child tasks */}
                      {isExpanded && children.length > 0 && (
                        <div className="ml-8 border-l-2 border-blue-300 dark:border-blue-700 pl-4 mt-1 space-y-1">
                          {children.map((child: any) => (
                            <div
                              key={child.id}
                              className="flex items-center gap-3 p-2 border rounded-md hover:bg-muted/50 cursor-pointer text-sm"
                              onClick={() => openTicket(child.id)}
                            >
                              <span className="font-mono text-xs text-muted-foreground">{child.taskNumber}</span>
                              <Badge className={`text-xs ${priorityColors[child.priority as keyof typeof priorityColors]}`}>{String(child.priority).toUpperCase()}</Badge>
                              <span className="flex-1 truncate">{child.title}</span>
                              <Badge className={`text-xs ${statusColors[child.status as keyof typeof statusColors]}`}>{String(child.status).replace("_", " ").toUpperCase()}</Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                {pagination && (
                  <div className="flex items-center justify-between pt-2">
                    <div className="text-xs text-muted-foreground">Page {pagination.currentPage} of {pagination.totalPages}</div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" disabled={!pagination.hasPrevPage} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</Button>
                      <Button variant="outline" size="sm" disabled={!pagination.hasNextPage} onClick={() => setPage((p) => p + 1)}>Next</Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Ticket Detail Dialog */}
      <Dialog
        open={isDetailDialogOpen}
        onOpenChange={(open) => {
          setIsDetailDialogOpen(open);
          if (!open) {
            setSelectedTicketId(null);
            setIsEditMode(false);
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              {isFetchingDetail
                ? "Loading..."
                : ticketDetail
                ? `${ticketDetail.taskNumber || ticketDetail.ticketNumber} • ${ticketDetail.title}`
                : "Task"}
            </DialogTitle>
          </DialogHeader>

          {ticketDetail && (<>
            <div className="flex-1 overflow-y-auto space-y-6 pr-1">
              {/* Top info — editable priority/status pills + created timestamp */}
              <div className="flex flex-wrap gap-2 items-center">
                <TaskHeaderPills
                  status={ticketDetail.status}
                  priority={ticketDetail.priority}
                  onStatusChange={(v) => updateTicketMutation.mutate({ status: v })}
                  onPriorityChange={(v) => updateTicketMutation.mutate({ priority: v })}
                />
                <div className="text-xs text-muted-foreground ml-auto">
                  Created {format(new Date(ticketDetail.createdAt), "PP p")}
                </div>
              </div>

              {/* Parent task link (for child tasks) */}
              {ticketDetail.parentTask && (
                <div className="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-950/20 rounded-md text-sm">
                  <GitMerge className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-muted-foreground">Subtask of</span>
                  <button
                    className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                    onClick={() => setSelectedTicketId(ticketDetail.parentTask!.id)}
                  >
                    {ticketDetail.parentTask.taskNumber} - {ticketDetail.parentTask.title}
                  </button>
                  <Badge className={`text-xs ml-auto ${statusColors[ticketDetail.parentTask.status as keyof typeof statusColors]}`}>
                    {String(ticketDetail.parentTask.status).replace("_", " ").toUpperCase()}
                  </Badge>
                </div>
              )}

              {/* Editable controls — inline chips (assignee / client / due) */}
              <TaskChipRow
                assignee={(ticketDetail as any).assignedTo ?? null}
                onAssigneeChange={(id) =>
                  updateTicketMutation.mutate({ assignedTo: id } as any)
                }
                due={(ticketDetail as any).dueDate ?? null}
                onDueChange={(d) =>
                  updateTicketMutation.mutate({
                    dueDate: d ? format(d, "yyyy-MM-dd") : null,
                  } as any)
                }
                clientName={(ticketDetail as any).clientName}
                people={teamMembers}
              />

              {/* Request Details — grouped card with requester, elections, QLE info */}
              {(() => {
                const cf = (ticketDetail as any)?.customFields || {};
                const enrollments = Array.isArray(cf?.requestedEnrollments) ? cf.requestedEnrollments : [];
                const hasEnrollments = enrollments.length > 0;
                const waivers = Array.isArray(cf?.requestedWaivers) ? cf.requestedWaivers : [];
                const hasWaivers = waivers.length > 0;
                const effDate = cf?.effectiveDate;
                const hasQle = !!cf.qleId;
                const formatTier = (tier: string) =>
                  (tier || "employee_only").replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase());

                // Description — strip "Requested items:" block when badges replace it
                let desc = ticketDetail.description || "";
                if (hasEnrollments) {
                  desc = desc.replace(/\n*Requested items:[\s\S]*$/, "").trimEnd();
                }

                // If nothing special, show basic requester + description
                if (!hasEnrollments && !hasQle) {
                  return (
                    <>
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <div className="text-sm text-muted-foreground mb-1">Requester</div>
                        <div className="font-medium">{ticketDetail.requesterName}</div>
                        {requesterSubtitle(ticketDetail) && (
                          <div className="text-sm text-muted-foreground">{requesterSubtitle(ticketDetail)}</div>
                        )}
                      </div>
                      {desc && (
                        <div>
                          <div className="text-sm font-medium mb-1">Description</div>
                          <p className="text-sm whitespace-pre-wrap text-muted-foreground">{desc}</p>
                        </div>
                      )}
                    </>
                  );
                }

                // Grouped card for enrollment requests / QLE tasks
                return (
                  <div className="rounded-lg border overflow-hidden">
                    {/* Header — requester */}
                    <div className="px-4 py-3 bg-muted/50 border-b">
                      <div className="font-medium text-sm">{ticketDetail.requesterName}</div>
                      {requesterSubtitle(ticketDetail) && (
                        <div className="text-xs text-muted-foreground">{requesterSubtitle(ticketDetail)}</div>
                      )}
                    </div>

                    {/* Effective date — prominent banner */}
                    {effDate && (
                      <div className="px-4 py-2.5 bg-blue-50 dark:bg-blue-950 border-b flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                          Effective Date: {(parseDateLocal(effDate) ?? new Date(effDate)).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                        </span>
                      </div>
                    )}

                    {/* Resolution banner */}
                    {(ticketDetail as any).resolution && (
                      <div className="px-4 py-2.5 bg-green-50 dark:bg-green-950 border-b flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                        <span className="text-sm font-medium text-green-900 dark:text-green-100">
                          {(ticketDetail as any).resolution}
                        </span>
                      </div>
                    )}

                    <div className="p-4 space-y-4">
                      {/* QLE Details */}
                      {hasQle && (
                        <div className="space-y-2">
                          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Qualifying Life Event</div>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                            <div><span className="text-muted-foreground">Event:</span> <span className="font-medium capitalize">{(cf.eventType || "N/A").replace(/_/g, " ")}</span></div>
                            {cf.eventDate && (
                              <div><span className="text-muted-foreground">Date:</span> <span className="font-medium">{new Date(cf.eventDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span></div>
                            )}
                          </div>
                          {cf.description && (
                            <div>
                              <div className="text-xs text-muted-foreground mb-1">Employee Notes</div>
                              <p className="text-sm whitespace-pre-wrap bg-muted/30 rounded p-2">{cf.description}</p>
                            </div>
                          )}
                          {cf.documentId ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={async () => {
                                try {
                                  const res = await apiRequest("GET", `/api/documents/${cf.documentId}/download?disposition=inline`);
                                  const data = await res.json();
                                  if (data.downloadUrl) window.open(data.downloadUrl, "_blank");
                                } catch {
                                  toast({ title: "Error", description: "Failed to open document", variant: "destructive" });
                                }
                              }}
                            >
                              <FileText className="h-3 w-3 mr-1" /> View Documentation
                            </Button>
                          ) : (
                            <div className="text-xs text-orange-600">No documentation uploaded</div>
                          )}
                        </div>
                      )}

                      {/* Divider between QLE and elections */}
                      {hasQle && hasEnrollments && <Separator />}

                      {/* Requested Elections */}
                      {hasEnrollments && (
                        <div className="space-y-2">
                          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Requested Elections</div>
                          <div className="space-y-1.5">
                            {enrollments.map((e: any, i: number) => (
                              <div key={i} className="flex items-center justify-between rounded-md border px-3 py-2 bg-muted/30">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground w-14">{e.benefitType}</span>
                                  <span className="font-medium text-sm">{e.planName || `Plan #${e.planId}`}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs bg-gray-100 dark:bg-gray-800 rounded px-2 py-0.5">{formatTier(e.coverageTier)}</span>
                                  {(e.dependentIds?.length || 0) > 0 && (
                                    <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded px-2 py-0.5">
                                      +{e.dependentIds.length} dep{e.dependentIds.length > 1 ? "s" : ""}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Waived plans */}
                      {hasWaivers && (
                        <div className="space-y-2">
                          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Waived</div>
                          <div className="space-y-1.5">
                            {waivers.map((w: any, i: number) => (
                              <div key={i} className="flex items-center justify-between rounded-md border px-3 py-2 bg-muted/30">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="font-medium text-sm text-muted-foreground line-through truncate">{w.planName || `Plan #${w.planId}`}</span>
                                  {w.waiverReason && (
                                    <span className="text-xs text-muted-foreground truncate">— {getWaiveReasonLabel(w.waiverReason)}</span>
                                  )}
                                </div>
                                <span className="text-xs bg-gray-100 dark:bg-gray-800 rounded px-2 py-0.5 shrink-0">Waived</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Non-enrollment description */}
                      {!hasEnrollments && desc && (
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Description</div>
                          <p className="text-sm whitespace-pre-wrap text-muted-foreground">{desc}</p>
                        </div>
                      )}

                      {/* Action buttons moved to sticky footer below */}
                    </div>
                  </div>
                );
              })()}

              {/* Enrollment Deny Dialog */}
              <AlertDialog open={enrollDenyDialogOpen} onOpenChange={setEnrollDenyDialogOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{denyTarget === "qle" ? "Deny Qualifying Life Event" : "Deny Enrollment Request"}</AlertDialogTitle>
                    <AlertDialogDescription>
                      The employee will be notified that their {denyTarget === "qle" ? "qualifying life event" : "enrollment"} was denied.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <Textarea
                    placeholder="Reason for denial (optional)"
                    value={enrollDenyReason}
                    onChange={(e) => setEnrollDenyReason(e.target.value)}
                  />
                  <div className="flex justify-end gap-2">
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={async () => {
                        try {
                          await apiRequest("POST", `/api/tasks/${selectedTicketId}/${denyTarget === "qle" ? "deny-qle" : "deny-enrollment"}`, { reason: enrollDenyReason || undefined });
                          await Promise.all([
                            invalidateTaskQueries(),
                            refetchTicketDetail(),
                          ]);
                          toast({ title: denyTarget === "qle" ? "Qualifying event denied" : "Enrollment denied", description: "Employee has been notified." });
                        } catch (e: any) {
                          toast({ title: "Error", description: e?.message || "Failed to deny request", variant: "destructive" });
                        }
                        setEnrollDenyDialogOpen(false);
                      }}
                    >
                      {denyTarget === "qle" ? "Deny Event" : "Deny Enrollment"}
                    </AlertDialogAction>
                  </div>
                </AlertDialogContent>
              </AlertDialog>
              {/* Checklist */}
              {ticketDetail.checklistItems && ticketDetail.checklistItems.length > 0 && (() => {
                const items = ticketDetail.checklistItems!;
                const completed = items.filter(i => i.isCompleted).length;
                const total = items.length;
                const pct = Math.round((completed / total) * 100);
                return (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <ListChecks className="h-4 w-4" />
                        Checklist
                      </div>
                      <span className="text-xs text-muted-foreground">{completed} / {total}</span>
                    </div>
                    <Progress value={pct} className="h-2" />
                    <div className="space-y-1">
                      {items.map(item => (
                        <label
                          key={item.id}
                          className="flex items-center gap-2 py-1 px-2 rounded hover:bg-muted/50 cursor-pointer"
                        >
                          <Checkbox
                            checked={item.isCompleted}
                            onCheckedChange={(checked) => {
                              toggleChecklistMutation.mutate({
                                taskId: ticketDetail.id,
                                itemId: item.id,
                                isCompleted: !!checked,
                              });
                            }}
                          />
                          <span className={`text-sm ${item.isCompleted ? 'line-through text-muted-foreground' : ''}`}>
                            {item.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Comments */}
              <div className="space-y-2">
                <div className="text-sm font-medium">Comments{(ticketDetail.comments?.length ?? 0) > 0 ? ` (${ticketDetail.comments!.length})` : ""}</div>
                {(ticketDetail.comments?.length ?? 0) > 0 && (
                  <div className="space-y-2 max-h-[30vh] overflow-y-auto pr-2">
                    {ticketDetail.comments!.map((c) => (
                      <div key={c.id} className="border rounded p-2">
                        <div className="text-xs text-muted-foreground flex justify-between">
                          <span>
                            {c.authorName} •{" "}
                            {format(new Date(c.createdAt), "PP p")}
                          </span>
                          {c.isInternal && (
                            <span className="italic">Internal</span>
                          )}
                        </div>
                        <div className="text-sm mt-1">{c.content}</div>
                      </div>
                    ))}
                  </div>
                )}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const fd = new FormData(e.currentTarget as HTMLFormElement);
                    const content = String(fd.get("content") || "").trim();
                    if (!content) return;
                    addCommentMutation.mutate({ content });
                    (e.currentTarget as HTMLFormElement).reset();
                  }}
                  className="flex gap-2"
                >
                  <Textarea
                    name="content"
                    placeholder="Add a comment..."
                    rows={1}
                    className="flex-1 min-h-[36px]"
                  />
                  <Button
                    type="submit"
                    size="sm"
                    disabled={addCommentMutation.isPending}
                  >
                    Post
                  </Button>
                </form>
              </div>

              {/* Subtasks */}
              {(() => {
                const subtasks = ticketDetail.subtasks || [];
                const doneCount = subtasks.filter(s => ['resolved', 'closed'].includes(s.status)).length;
                const isOpen = subtasksOpen || subtasks.length > 0;
                return (
                  <div className="space-y-3">
                    <button
                      className="flex items-center justify-between w-full text-left"
                      onClick={() => setSubtasksOpen(!isOpen)}
                    >
                      <div className="flex items-center gap-2 text-sm font-medium">
                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        <GitMerge className="h-4 w-4" />
                        Subtasks
                        {subtasks.length > 0 && (
                          <Badge variant="secondary" className="text-xs ml-1">{doneCount}/{subtasks.length}</Badge>
                        )}
                      </div>
                      {isOpen && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCreateSubtaskParent({
                              id: ticketDetail.id,
                              clientId: ticketDetail.clientId,
                              taskNumber: ticketDetail.taskNumber || ticketDetail.ticketNumber || `#${ticketDetail.id}`,
                            });
                            setIsCreateDialogOpen(true);
                          }}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add
                        </Button>
                      )}
                    </button>
                    {isOpen && subtasks.length > 0 && (
                      <div className="space-y-1">
                        {subtasks.map(sub => (
                          <div
                            key={sub.id}
                            className="flex items-center gap-3 p-2 border rounded-md hover:bg-muted/50 text-sm group"
                          >
                            <span
                              className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                              onClick={() => setSelectedTicketId(sub.id)}
                            >
                              <span className="font-mono text-xs text-muted-foreground">{sub.taskNumber}</span>
                              <Badge className={`text-xs ${priorityColors[sub.priority as keyof typeof priorityColors]}`}>{String(sub.priority).toUpperCase()}</Badge>
                              <span className="flex-1 truncate">{sub.title}</span>
                              <Badge className={`text-xs ${statusColors[sub.status as keyof typeof statusColors]}`}>{String(sub.status).replace("_", " ").toUpperCase()}</Badge>
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive shrink-0"
                              title="Unlink subtask"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await apiRequest("PATCH", `/api/tasks/${sub.id}`, { parentTaskId: null });
                                  await Promise.all([
                                    refetchTicketDetail(),
                                    invalidateTaskQueries(),
                                  ]);
                                  toast({ title: "Unlinked", description: `${sub.taskNumber} removed as subtask`, variant: "success" });
                                } catch (err: any) {
                                  toast({ title: "Error", description: err.message || "Failed to unlink subtask", variant: "destructive" });
                                }
                              }}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Link existing task */}
                    {isOpen && <div className="flex items-center gap-2">
                      <Input
                        placeholder="Task number (e.g. TSK-2025-ABC)"
                        value={linkTaskNumber}
                        onChange={(e) => setLinkTaskNumber(e.target.value)}
                        className="h-8 text-xs flex-1"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs"
                        disabled={!linkTaskNumber.trim() || isLinking}
                        onClick={async () => {
                          if (!linkTaskNumber.trim() || !ticketDetail) return;
                          setIsLinking(true);
                          try {
                            // Search for the task by number
                            const searchRes = await fetch(`/api/tasks?q=${encodeURIComponent(linkTaskNumber.trim())}&limit=5`, { credentials: "include" });
                            if (!searchRes.ok) throw new Error("Search failed");
                            const searchData = await searchRes.json();
                            const match = searchData.tasks?.find((t: any) => t.taskNumber === linkTaskNumber.trim());
                            if (!match) {
                              toast({ title: "Not found", description: `No task found with number "${linkTaskNumber.trim()}"`, variant: "destructive" });
                              return;
                            }
                            if (match.id === ticketDetail.id) {
                              toast({ title: "Error", description: "A task cannot be a subtask of itself", variant: "destructive" });
                              return;
                            }
                            // PATCH the found task to set its parentTaskId
                            const patchRes = await apiRequest("PATCH", `/api/tasks/${match.id}`, { parentTaskId: ticketDetail.id });
                            await patchRes.json();
                            await Promise.all([
                              refetchTicketDetail(),
                              invalidateTaskQueries(),
                            ]);
                            setLinkTaskNumber("");
                            toast({ title: "Linked", description: `${match.taskNumber} linked as subtask`, variant: "success" });
                          } catch (err: any) {
                            toast({ title: "Error", description: err.message || "Failed to link task", variant: "destructive" });
                          } finally {
                            setIsLinking(false);
                          }
                        }}
                      >
                        <Link className="h-3 w-3 mr-1" />
                        {isLinking ? "Linking..." : "Link"}
                      </Button>
                    </div>}
                  </div>
                );
              })()}

              {/* History — collapsed by default */}
              <div className="space-y-2">
                <button
                  className="flex items-center gap-2 text-sm font-medium w-full text-left"
                  onClick={() => setHistoryOpen(!historyOpen)}
                >
                  {historyOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  History
                  {(ticketDetail.history?.length ?? 0) > 0 && (
                    <Badge variant="secondary" className="text-xs">{ticketDetail.history!.length}</Badge>
                  )}
                </button>
                {historyOpen && (
                  <div className="space-y-1 max-h-[30vh] overflow-y-auto pr-2 text-xs text-muted-foreground">
                    {ticketDetail.history?.length ? (
                      ticketDetail.history.map((h) => (
                        <div key={h.id}>
                          {format(new Date(h.createdAt), "PP p")} •{" "}
                          {h.changeType.replace("_", " ")}{" "}
                          {h.fieldName
                            ? `(${h.fieldName}: ${h.oldValue ?? ""} → ${
                                h.newValue ?? ""
                              })`
                            : ""}{" "}
                          — {h.description}
                        </div>
                      ))
                    ) : (
                      <div>No history yet</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Sticky action footer */}
            {(() => {
              if (!ticketDetail) return null;
              const isResolved =
                String(ticketDetail.status) === "resolved" ||
                String(ticketDetail.status) === "closed";
              if (isResolved || !selectedTicketId) return null;

              const isEnroll = isEnrollmentRequestTask(ticketDetail);
              const isWaiver =
                (ticketDetail as any)?.category === "plan_changes" &&
                (ticketDetail as any)?.subcategory === "waiver_request";
              // QLE-processing task: linked to a QLE but not an enrollment/waiver request.
              const qleCf: any = (ticketDetail as any)?.customFields || {};
              const hasEnrollmentItems =
                Array.isArray(qleCf?.requestedEnrollments) && qleCf.requestedEnrollments.length > 0;
              const isQle = !!qleCf.qleId && !isEnroll && !isWaiver && !hasEnrollmentItems;

              if (!isEnroll && !isWaiver && !isQle) return null;

              // Already approved: enrollments exist, task is in_progress. Hide
              // Approve/Deny (no double-approve) and prompt to finish the checklist.
              const enrollmentApproved =
                (ticketDetail as any)?.customFields?.enrollmentApproved === true;
              if (isEnroll && enrollmentApproved) {
                return (
                  <div className="border-t bg-background pt-3 pb-1 shrink-0">
                    <div className="flex items-start gap-2 rounded-md border border-green-300 bg-green-50 px-3 py-2.5 text-sm text-green-800 dark:border-green-800 dark:bg-green-900/30 dark:text-green-300">
                      <CheckCircle className="h-4 w-4 mt-0.5 shrink-0" />
                      <div>
                        <span className="font-semibold">Enrollment approved.</span>{" "}
                        Finish the checklist above, then close this task.
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div className="border-t bg-background pt-3 pb-1 flex items-center justify-end gap-2 shrink-0">
                  {isWaiver && (
                    <Button
                      variant="destructive"
                      onClick={async () => {
                        try {
                          await apiRequest("POST", `/api/tasks/${selectedTicketId}/approve-waiver`, {});
                          await Promise.all([
                            invalidateTaskQueries(),
                            refetchTicketDetail(),
                          ]);
                          toast({ title: "Waiver approved", description: "Enrollment has been terminated." });
                        } catch (e: any) {
                          toast({ title: "Error", description: e?.message || "Failed to approve waiver", variant: "destructive" });
                        }
                      }}
                    >
                      Approve Waiver
                    </Button>
                  )}
                  {isEnroll && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => { setDenyTarget("enrollment"); setEnrollDenyReason(""); setEnrollDenyDialogOpen(true); }}
                      >
                        Deny
                      </Button>
                      <Button
                        onClick={async () => {
                          try {
                            await apiRequest("POST", `/api/tasks/${selectedTicketId}/approve-enrollment`, {});
                            await Promise.all([
                              invalidateTaskQueries(),
                              refetchTicketDetail(),
                            ]);
                            toast({ title: "Enrollment approved", description: "Enrollments created. Finish the checklist, then close the task.", variant: "success" });
                          } catch (e: any) {
                            toast({ title: "Error", description: e?.message || "Failed to approve enrollment", variant: "destructive" });
                          }
                        }}
                      >
                        Approve Enrollment
                      </Button>
                    </>
                  )}
                  {isQle && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => { setDenyTarget("qle"); setEnrollDenyReason(""); setEnrollDenyDialogOpen(true); }}
                      >
                        Deny
                      </Button>
                      <Button
                        onClick={async () => {
                          try {
                            await apiRequest("POST", `/api/tasks/${selectedTicketId}/approve-qle`, {});
                            await Promise.all([
                              invalidateTaskQueries(),
                              refetchTicketDetail(),
                            ]);
                            toast({ title: "Qualifying event approved", description: "The employee can now make benefit changes.", variant: "success" });
                          } catch (e: any) {
                            toast({ title: "Error", description: e?.message || "Failed to approve qualifying event", variant: "destructive" });
                          }
                        }}
                      >
                        Approve Event
                      </Button>
                    </>
                  )}
                </div>
              );
            })()}
          </>)}
        </DialogContent>
      </Dialog>

      {/* Close parent warning dialog */}
      <AlertDialog open={!!pendingCloseWarning} onOpenChange={(open) => { if (!open) setPendingCloseWarning(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Open subtasks remaining</AlertDialogTitle>
            <AlertDialogDescription>
              This task has {pendingCloseWarning?.openCount} open subtask(s). Are you sure you want to close it without resolving them first?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex justify-end gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingCloseWarning) {
                  updateTicketMutation.mutate({ status: pendingCloseWarning.status, forceClose: true } as any);
                  setPendingCloseWarning(null);
                }
              }}
            >
              Close Anyway
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
